import React, { useState } from 'react';
import { TrendingUp, Save, BookOpen as BookOpenIcon } from 'lucide-react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency, shortenLargeNumber } from '../../utils/formatters';
import DynamicChartSystem from './DynamicChartSystem';
import MultiplierCard from './MultiplierCard';
import TeachingModule from '../TeachingModule';
import { teachingContent } from '../../data/teachingContent';

const PatrimonyChart: React.FC<PatrimonyChartProps> = () => {
  const { 
    investment, 
    results, 
    patrimonyChartVisibility, 
    togglePatrimonyChartElement,
    resetPatrimonyChartVisibility
  } = useCalculator();
  
  const [showTeachingModule, setShowTeachingModule] = useState(false);

  if (!results) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">
            {investment.language === 'es' ? 'Cargando gráficos...' :
             investment.language === 'en' ? 'Loading charts...' :
             'Chargement des graphiques...'}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {investment.language === 'es' ? 'Procesando datos de patrimonio' :
             investment.language === 'en' ? 'Processing patrimony data' :
             'Traitement des données de patrimoine'}
          </p>
        </div>
      </div>
    );
  }

  // Prepare data for the chart - always include all data points
  const allChartData = results.yearlyData?.map(data => ({
    year: data.year,
    'Cosecha Capital': patrimonyChartVisibility.showReal ? data.realCitrusPatrimony : data.citrusPatrimony,
    'CETES': patrimonyChartVisibility.showReal ? (data.realCetesPatrimony || data.cetesPatrimony / Math.pow(1 + investment.inflationRate / 100, data.year)) : data.cetesPatrimony,
    'Ahorro Tradicional': patrimonyChartVisibility.showReal ? (data.realSavingsPatrimony || data.savingsPatrimony / Math.pow(1 + investment.inflationRate / 100, data.year)) : data.savingsPatrimony,
    'Bienes Raíces': patrimonyChartVisibility.showReal ? (data.realEstateRealPatrimony || data.realEstatePatrimony / Math.pow(1 + investment.inflationRate / 100, data.year)) : data.realEstatePatrimony,
    'Capital Aportado': data.contributedCapital
  })) || [];

  // Prepare summary data first
  const summaryData = {
    patrimony: [
      {
        label: `Cosecha Capital ${patrimonyChartVisibility.showReal ? '(Real)' : ''}`,
        value: formatCurrency(patrimonyChartVisibility.showReal ? results.realFinalPatrimony : results.finalPatrimony, investment.currencyFormat),
        color: 'text-green-600 bg-green-50'
      },
      {
        label: `CETES ${patrimonyChartVisibility.showReal ? '(Real)' : ''}`,
        value: formatCurrency(patrimonyChartVisibility.showReal ? results.realCetesPatrimony : results.cetesPatrimony, investment.currencyFormat),
        color: 'text-blue-600 bg-blue-50'
      },
      {
        label: `Ahorro ${patrimonyChartVisibility.showReal ? '(Real)' : ''}`,
        value: formatCurrency(patrimonyChartVisibility.showReal ? results.realSavingsPatrimony : results.savingsPatrimony, investment.currencyFormat),
        color: 'text-purple-600 bg-purple-50'
      },
      {
        label: `Bienes Raíces ${patrimonyChartVisibility.showReal ? '(Real)' : ''}`,
        value: formatCurrency(patrimonyChartVisibility.showReal ? results.realRealEstatePatrimony : results.realEstatePatrimony, investment.currencyFormat),
        color: 'text-orange-600 bg-orange-50'
      },
      {
        label: 'Valor EBITDA',
        value: formatCurrency(results.finalEbitdaValue, investment.currencyFormat),
        color: 'text-amber-600 bg-amber-50'
      }
    ]
  };

  // Define chart elements with extremely subtle appearance when toggled off
  const chartElements = [
    {
      dataKey: 'Cosecha Capital',
      name: `Cosecha Capital ${patrimonyChartVisibility.showReal ? '(Real)' : ''}`,
      color: patrimonyChartVisibility.cosechaCapital ? '#16a34a' : '#e0e0e0',
      strokeWidth: patrimonyChartVisibility.cosechaCapital ? 3 : 0.5,
      fillOpacity: patrimonyChartVisibility.cosechaCapital ? 0.6 : 0.01,
      strokeDasharray: patrimonyChartVisibility.cosechaCapital ? undefined : '1 5'
    },
    {
      dataKey: 'Capital Aportado',
      name: 'Capital Aportado',
      color: patrimonyChartVisibility.contributedCapital ? '#94a3b8' : '#e0e0e0',
      strokeDasharray: patrimonyChartVisibility.contributedCapital ? '5 5' : '1 5',
      strokeWidth: patrimonyChartVisibility.contributedCapital ? 2 : 0.5,
      fillOpacity: patrimonyChartVisibility.contributedCapital ? 0.3 : 0.01
    },
    {
      dataKey: 'CETES',
      name: 'CETES',
      color: patrimonyChartVisibility.cetes ? '#0284c7' : '#e0e0e0',
      strokeDasharray: patrimonyChartVisibility.cetes ? '5 5' : '1 5',
      strokeWidth: patrimonyChartVisibility.cetes ? 2 : 0.5,
      fillOpacity: patrimonyChartVisibility.cetes ? 0.4 : 0.01
    },
    {
      dataKey: 'Ahorro Tradicional',
      name: 'Ahorro Tradicional',
      color: patrimonyChartVisibility.savings ? '#7c3aed' : '#e0e0e0',
      strokeDasharray: patrimonyChartVisibility.savings ? '5 5' : '1 5',
      strokeWidth: patrimonyChartVisibility.savings ? 2 : 0.5,
      fillOpacity: patrimonyChartVisibility.savings ? 0.4 : 0.01
    },
    {
      dataKey: 'Bienes Raíces',
      name: 'Bienes Raíces',
      color: patrimonyChartVisibility.realEstate ? '#ea580c' : '#e0e0e0',
      strokeDasharray: patrimonyChartVisibility.realEstate ? '5 5' : '1 5',
      strokeWidth: patrimonyChartVisibility.realEstate ? 2 : 0.5,
      fillOpacity: patrimonyChartVisibility.realEstate ? 0.4 : 0.01
    }
  ];

  // Prepare data for pie chart (final year composition)
  const pieData = [
    {
      name: `Cosecha Capital ${patrimonyChartVisibility.showReal ? '(Real)' : ''}`,
      value: patrimonyChartVisibility.showReal ? results.realFinalPatrimony : results.finalPatrimony,
      color: patrimonyChartVisibility.cosechaCapital ? '#16a34a' : '#e0e0e0'
    },
    {
      name: 'CETES',
      value: results.cetesPatrimony,
      color: patrimonyChartVisibility.cetes ? '#0284c7' : '#e0e0e0'
    },
    {
      name: 'Ahorro Tradicional',
      value: results.savingsPatrimony,
      color: patrimonyChartVisibility.savings ? '#7c3aed' : '#e0e0e0'
    },
    {
      name: 'Bienes Raíces',
      value: results.realEstatePatrimony,
      color: patrimonyChartVisibility.realEstate ? '#ea580c' : '#e0e0e0'
    }
  ];

  // Prepare chart system configuration
  const chartCategories = [
    {
      id: 'patrimony',
      title: 'Evolución del Patrimonio',
      description: 'Crecimiento patrimonial a lo largo del tiempo con controles de animación',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'from-green-500 to-green-600',
      availableTypes: ['line', 'bar', 'area', 'pie'] as const
    }
  ];

  const chartSystemData = {
    patrimony: allChartData
  };

  const chartSystemElements = {
    patrimony: chartElements
  };

  const pieChartData = {
    patrimony: pieData
  };

  // Prepare visibility controls for DynamicChartSystem
  const visibilityControls = {
    patrimony: {
      visibility: patrimonyChartVisibility,
      toggle: togglePatrimonyChartElement
    }
  };

  // Prepare reset functions for DynamicChartSystem
  const resetVisibilityFunctions = {
    patrimony: resetPatrimonyChartVisibility
  };

  const saveVisualization = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
        <head>
          <meta charset="UTF-8">
          <title>Evolución del Patrimonio - Cosecha Capital</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              line-height: 1.5;
              margin: 0;
              padding: 20px;
              max-width: 1200px;
              margin: 0 auto;
            }
            h1 {
              color: #16a34a;
              margin-bottom: 20px;
            }
            p {
              color: #4b5563;
              margin-bottom: 30px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th, td {
              padding: 12px;
              text-align: right;
              border-bottom: 1px solid #e5e7eb;
            }
            th:first-child, td:first-child {
              text-align: left;
            }
            th {
              background-color: #f0fdf4;
              color: #166534;
              font-weight: 600;
            }
            tr:nth-child(even) {
              background-color: #f9fafb;
            }
            .notes {
              background-color: #f3f4f6;
              padding: 20px;
              border-radius: 8px;
              margin-top: 30px;
            }
            .notes h3 {
              color: #374151;
              margin-bottom: 10px;
            }
            .notes ul {
              color: #4b5563;
              padding-left: 20px;
            }
          </style>
        </head>
        <body>
          <h1>Evolución del Patrimonio - Cosecha Capital</h1>
          <p>Análisis detallado de la evolución patrimonial durante ${investment.years} años de inversión.</p>
          
          <table>
            <thead>
              <tr>
                <th>Año</th>
                ${patrimonyChartVisibility.cosechaCapital ? 
                  `<th>Cosecha Capital${patrimonyChartVisibility.showReal ? ' (Real)' : ''}</th>` : ''}
                ${patrimonyChartVisibility.cetes ? 
                  `<th>CETES${patrimonyChartVisibility.showReal ? ' (Real)' : ''}</th>` : ''}
                ${patrimonyChartVisibility.savings ? 
                  `<th>Ahorro${patrimonyChartVisibility.showReal ? ' (Real)' : ''}</th>` : ''}
                ${patrimonyChartVisibility.realEstate ? 
                  `<th>Bienes Raíces${patrimonyChartVisibility.showReal ? ' (Real)' : ''}</th>` : ''}
                ${patrimonyChartVisibility.contributedCapital ? 
                  `<th>Capital Aportado</th>` : ''}
                ${patrimonyChartVisibility.ebitda ? 
                  `<th>Valor EBITDA</th>` : ''}
              </tr>
            </thead>
            <tbody>
              ${results.yearlyData.map(data => `
                <tr>
                  <td>${data.year}</td>
                  ${patrimonyChartVisibility.cosechaCapital ? 
                    `<td>${formatCurrency(patrimonyChartVisibility.showReal ? data.realCitrusPatrimony : data.citrusPatrimony, investment.currencyFormat)}</td>` : ''}
                  ${patrimonyChartVisibility.cetes ? 
                    `<td>${formatCurrency(patrimonyChartVisibility.showReal ? 
                      (data.realCetesPatrimony || data.cetesPatrimony / Math.pow(1 + investment.inflationRate / 100, data.year)) : 
                      data.cetesPatrimony, investment.currencyFormat)}</td>` : ''}
                  ${patrimonyChartVisibility.savings ? 
                    `<td>${formatCurrency(patrimonyChartVisibility.showReal ? 
                      (data.realSavingsPatrimony || data.savingsPatrimony / Math.pow(1 + investment.inflationRate / 100, data.year)) : 
                      data.savingsPatrimony, investment.currencyFormat)}</td>` : ''}
                  ${patrimonyChartVisibility.realEstate ? 
                    `<td>${formatCurrency(patrimonyChartVisibility.showReal ? 
                      (data.realEstateRealPatrimony || data.realEstatePatrimony / Math.pow(1 + investment.inflationRate / 100, data.year)) : 
                      data.realEstatePatrimony, investment.currencyFormat)}</td>` : ''}
                  ${patrimonyChartVisibility.contributedCapital ? 
                    `<td>${formatCurrency(data.contributedCapital, investment.currencyFormat)}</td>` : ''}
                  ${patrimonyChartVisibility.ebitda ? 
                    `<td>${formatCurrency(data.ebitdaValue || 0, investment.currencyFormat)}</td>` : ''}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="notes">
            <h3>Notas:</h3>
            <ul>
              <li>Período de análisis: ${investment.years} años</li>
              <li>Tasa de inflación considerada: ${investment.inflationRate}%</li>
              <li>Rendimiento anual Cosecha Capital: ${investment.annualProfit}%</li>
              <li>Los valores patrimoniales incluyen la apreciación de los certificados</li>
              <li>Se considera la reinversión de utilidades cuando está activada</li>
              <li>${investment.increaseLemonPrice ? 
                `Incremento anual del precio del limón: ${investment.lemonPriceIncrease}%` : 
                'Sin incremento en el precio del limón'}</li>
              <li>${patrimonyChartVisibility.showReal ? 
                'Los valores mostrados están ajustados por inflación (valores reales)' : 
                'Los valores mostrados son nominales (sin ajustar por inflación)'}</li>
              <li>Factor EBITDA: ${investment.ebitdaFactor}x (multiplicador aplicado a las utilidades anuales)</li>
            </ul>
          </div>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cosecha_capital_patrimonio_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-green-800">
          {investment.language === 'es' ? 'Evolución del Patrimonio' :
           investment.language === 'en' ? 'Patrimony Evolution' :
           'Évolution du Patrimoine'}
        </h3>
        <div className="flex items-center gap-3">
          <button
            onClick={saveVisualization}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            title={investment.language === 'es' ? 'Guardar esta visualización como HTML' :
                   investment.language === 'en' ? 'Save this visualization as HTML' :
                   'Sauvegarder cette visualisation en HTML'}
          >
            <Save className="w-5 h-5" />
            <span>
              {investment.language === 'es' ? 'Guardar' :
               investment.language === 'en' ? 'Save' :
               'Sauvegarder'}
            </span>
          </button>
          <button
            onClick={() => setShowTeachingModule(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            title={investment.language === 'es' ? 'Abrir tutorial de esta sección' :
                   investment.language === 'en' ? 'Open tutorial for this section' :
                   'Ouvrir le tutoriel pour cette section'}
          >
            <BookOpenIcon className="w-5 h-5" />
            <span>
              {investment.language === 'es' ? 'Tutorial' :
               investment.language === 'en' ? 'Tutorial' :
               'Tutoriel'}
            </span>
          </button>
        </div>
      </div>

      {/* Dynamic Chart System */}
      <div className="flex-1 min-h-[420px]">
        <DynamicChartSystem
          categories={chartCategories}
          chartData={chartSystemData}
          chartElements={chartSystemElements}
          pieData={pieChartData}
          formatters={{
            yLabel: (value) => shortenLargeNumber(value),
            tooltip: (value) => formatCurrency(value, investment.currencyFormat)
          }}
          summaryData={summaryData}
          investment={investment}
          multiplierValidChartIds={['patrimony']}
          visibilityControls={visibilityControls}
          resetVisibilityFunctions={resetVisibilityFunctions}
          hideAdditionalOptions={true}
        />
      </div>
      
      {/* Multiplier Card */}
      {summaryData.patrimony && investment && (
        <MultiplierCard 
          chartId="patrimony"
          summaryData={summaryData.patrimony}
          currencyFormat={investment.currencyFormat}
          validChartIds={['patrimony']}
        />
      )}
      
      {/* Teaching Module */}
      <TeachingModule
        isOpen={showTeachingModule}
        onClose={() => setShowTeachingModule(false)}
        title={teachingContent.patrimony.title}
        steps={teachingContent.patrimony.steps}
      />
    </div>
  );
};

export default PatrimonyChart;